import React, { useState } from 'react';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

interface AgentCategory {
  name: string;
  count: number;
  agents: string[];
}

const EquilateralLanding: React.FC = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [showCustomPricing, setShowCustomPricing] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const pricingTiers: PricingTier[] = [
    {
      name: 'Standard',
      price: '$4,999',
      period: '/month',
      features: [
        'All 62 AI agents',
        'All 18 features enabled',
        '11 pre-configured workflows',
        'Up to 50 users',
        'Standard integrations',
        'Email & chat support',
        '99.9% SLA'
      ],
      cta: 'Contact Sales'
    },
    {
      name: 'Enterprise',
      price: '$9,999',
      period: '/month',
      features: [
        'Everything in Standard',
        'Unlimited users',
        'Custom workflows',
        'Private cloud deployment',
        'Advanced security (SOC 2, ISO)',
        'Dedicated success team',
        'Custom integrations',
        '99.99% SLA'
      ],
      cta: 'Contact Sales',
      highlighted: true
    }
  ];

  const agentCategories: AgentCategory[] = [
    {
      name: 'Core Infrastructure',
      count: 10,
      agents: ['AgentFactoryAgent', 'AuditorAgent', 'DeploymentAgent', 'KnowledgeSynthesisAgent', 'PatternHarvestingAgent', 'ProjectStructureDiscoveryAgent', 'SecurityReviewerAgent', 'TestAgent', 'TestDataAgent', 'UIUXSpecialistAgent']
    },
    {
      name: 'Security & Compliance',
      count: 11,
      agents: ['BasicSecurityAssessmentAgent', 'BreachResponseAgent', 'ComplianceOrchestrationAgent', 'InsiderThreatAgent', 'PenetrationTestingAgent', 'SecurityAnalysisAgent', 'SecurityTriageAgent', 'SecurityVulnerabilityAgent', 'StandardsEnforcementAgent', 'StandardsSyncAgent', 'ThreatModelingSecurityAgent']
    },
    {
      name: 'Business Intelligence',
      count: 6,
      agents: ['BusinessDevelopmentAgent', 'BusinessIntelligenceAgent', 'CostIntelligenceAgent', 'EnhancedBusinessIntelligenceAgent', 'IntellectualPropertyAgent', 'MarketResearchAgent']
    },
    {
      name: 'Development & Code',
      count: 5,
      agents: ['BackendAuditorAgent', 'CodeGenerationAgent', 'ConfigurationManagementAgent', 'FrontendAuditorAgent', 'TemplateValidationAgent']
    },
    {
      name: 'Testing & Quality',
      count: 4,
      agents: ['DeploymentAuditorAgent', 'EnvironmentOptimizationAgent', 'SystematicEvaluationAgent', 'TestingOrchestrationAgent']
    },
    {
      name: 'Data & Privacy',
      count: 9,
      agents: ['ConsentManagementAgent', 'DataGovernanceAgent', 'DataMappingAgent', 'DataSubjectRightsAgent', 'EquilateralAITransferAgent', 'GovernmentDataProcessingAgent', 'PrivacyImpactAgent', 'PrivacyTrainingAgent', 'RetentionPolicyAgent']
    },
    {
      name: 'Deployment & Infrastructure',
      count: 2,
      agents: ['DatabaseAgent', 'EnhancedDeploymentAgent']
    },
    {
      name: 'Governance & Control',
      count: 3,
      agents: ['ControlTowerAgent', 'ControlTowerMasterAccountAgent', 'FocusedControlTowerAgent']
    },
    {
      name: 'Knowledge & Analysis',
      count: 5,
      agents: ['AgentHealthIntelligenceAgent', 'BiographerAgent', 'EnterpriseArchaeologistAgent', 'IntegrationKnowledgeAgent', 'LibrarianAgent']
    },
    {
      name: 'Orchestration & Workflow',
      count: 7,
      agents: ['CrossProjectDiscoveryAgent', 'IncidentResponseOrchestrationAgent', 'JimTodoCrossProjectAgent', 'PrioritizationAgent', 'ProjectManagementAgent', 'WorkflowMappingAgent', 'WorkflowRegistryAgent']
    }
  ];

  const features = [
    'Multi-agent orchestration',
    'Event-driven architecture',
    'Cost analysis gates',
    'Automatic standards enforcement',
    'Parallel execution (15 concurrent)',
    'Environment-aware thresholds',
    'Privacy-by-design automation',
    'Dynamic structure discovery',
    'Cross-agent workflows',
    'Distributed state management',
    'Multi-environment security',
    'Audit trail systems',
    'Cost attribution',
    'Dynamic resource allocation',
    'Performance analytics',
    'Agent-aware testing',
    'Model selection optimization',
    'Deployment orchestration'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              The Complete AI-Powered Engineering Platform
            </h1>
            <p className="text-xl md:text-2xl text-indigo-100 mb-8 max-w-3xl mx-auto">
              62 specialized AI agents. 18 enterprise features. 11 workflows.
              Transform your engineering organization with autonomous AI orchestration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="bg-white text-indigo-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-colors"
                onClick={() => setShowContactForm(true)}
              >
                Request Demo
              </button>
              <button
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
                onClick={() => setShowCustomPricing(true)}
              >
                Custom Pricing ($10K+)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-indigo-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white">62</div>
              <div className="text-indigo-200">AI Agents</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">18</div>
              <div className="text-indigo-200">Features</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">11</div>
              <div className="text-indigo-200">Workflows</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">10</div>
              <div className="text-indigo-200">Categories</div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Built for Strategic Engineering Leaders
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-indigo-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-indigo-800 mb-4">Enterprise Organizations</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Full platform deployment with all agents
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Custom workflows for your processes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Enterprise security and compliance
                </li>
              </ul>
            </div>
            <div className="bg-indigo-50 p-8 rounded-xl">
              <h3 className="text-xl font-semibold text-indigo-800 mb-4">Strategic Engineering Leaders</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Autonomous development operations
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Cost intelligence and optimization
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 mt-1">-</span>
                  Standards enforcement at scale
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Agent Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            62 Specialized AI Agents
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Organized in 10 categories, each agent is purpose-built for specific engineering tasks
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {agentCategories.map((category) => (
              <div key={category.name} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedCategory(expandedCategory === category.name ? null : category.name)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-gray-900">{category.name}</span>
                    <span className="bg-indigo-100 text-indigo-700 text-sm px-2 py-0.5 rounded-full">
                      {category.count}
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedCategory === category.name ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedCategory === category.name && (
                  <div className="px-6 pb-4 border-t">
                    <div className="pt-4 flex flex-wrap gap-2">
                      {category.agents.map((agent) => (
                        <span
                          key={agent}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                        >
                          {agent}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            18 Enterprise Features
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {features.map((feature) => (
              <div key={feature} className="bg-indigo-50 p-4 rounded-lg text-center">
                <span className="text-sm font-medium text-indigo-800">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Enterprise Pricing
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`bg-white rounded-xl shadow-sm p-8 ${
                  tier.highlighted ? 'ring-2 ring-indigo-600' : ''
                }`}
              >
                {tier.highlighted && (
                  <span className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mt-4">{tier.name}</h3>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600">{tier.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    tier.highlighted
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                  onClick={() => setShowContactForm(true)}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Need custom pricing for larger deployments ($10,000+/month)?
            </p>
            <button
              className="text-indigo-600 font-semibold hover:text-indigo-700"
              onClick={() => setShowCustomPricing(true)}
            >
              Contact for Custom Pricing
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Transform Your Engineering Organization
          </h2>
          <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join enterprises deploying 62 AI agents to automate development, security, and compliance.
          </p>
          <button
            className="bg-white text-indigo-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-50 transition-colors"
            onClick={() => setShowContactForm(true)}
          >
            Schedule Executive Briefing
          </button>
        </div>
      </section>

      {/* Custom Pricing Modal */}
      {showCustomPricing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Custom Enterprise Pricing</h3>
            <p className="text-gray-600 mb-6">
              For deployments exceeding $10,000/month, we offer custom pricing with:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-indigo-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Volume discounts</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-indigo-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Dedicated infrastructure</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-indigo-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Custom agent development</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-indigo-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">On-premise deployment options</span>
              </li>
            </ul>
            <div className="flex gap-4">
              <button
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                onClick={() => setShowCustomPricing(false)}
              >
                Close
              </button>
              <button
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={() => {
                  setShowCustomPricing(false);
                  setShowContactForm(true);
                }}
              >
                Get Quote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Enterprise Sales</h3>
            <p className="text-gray-600 mb-6">
              Our enterprise team will reach out within 24 hours.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
              <input
                type="email"
                placeholder="Work Email"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Company"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Title/Role"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
              />
              <select className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent">
                <option>Expected Monthly Budget</option>
                <option>$5,000 - $10,000</option>
                <option>$10,000 - $25,000</option>
                <option>$25,000 - $50,000</option>
                <option>$50,000+</option>
              </select>
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  onClick={() => setShowContactForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquilateralLanding;
